import React, {
	useEffect,
	useState,
	useMemo,
	useRef,
	useCallback,
} from 'react';
import { Radio, Modal, Button, Space, Tag, Tooltip, Input } from 'antd';
import {
	ArrowLeftOutlined,
	SwapOutlined,
	EditOutlined,
	SplitCellsOutlined,
	PlusOutlined,
	FilterOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { MaterialLengthsFilter } from '../../../components/MaterialLengths/filters';
import MaterialLengthsTable from '../../../components/MaterialLengths/MaterialLengthsTable';
import AppliedFiltersBar from '../../../components/MaterialLengths/AppliedFiltersBar';
import MaterialReservationModal from '../../../components/MaterialReservationModal';
import SplitLengthModal from '../../../components/MaterialInventory/SplitLengthModal';
import ModifyLengthsModal from '../../../components/MaterialLengths/ModifyLengthsModal';
import AddMaterialLengthsModal from '../../../components/MaterialInventory/AddMaterialLengthsModal';
import MoveMaterialModal from '../../../components/MaterialLengths/MoveMaterialModal';
import type { MoveMaterialSummaryData } from '../../../components/MaterialLengths/MoveMaterialModal';
import MaterialLocationsSummaryModal from '../../../components/Operations/SawCutQueue/SawCutQueueTable/MaterialLocationsSummaryModal';
import BuildingIndicator from '../../../components/BuildingIndicator';
import { Material } from '../../../interfaces/MaterialInventory.interface';
import { useBuildingContext } from '../../../context/BuildingContext';
import {
	useGetAllMaterialLengthsQuery,
	useDeleteMaterialLengthMutation,
	useRecordAttributionCostMutation,
	useGetMaterialCategoriesQuery,
	useGetMaterialShapesQuery,
	useGetMaterialTypesQuery,
	useGetMaterialLengthDimensionRangesQuery,
	useGetInventoryLocationsQuery,
	useGetMaterialShelvesQuery,
} from '../../../redux/api/material';

const { Search } = Input;

// ─── Query param keys the API accepts ────────────────────────────────────────
const FILTER_PARAM_KEYS = [
	'search',
	'shape',
	'category',
	'types',
	'inventory_location',
	'status',
	'has_certificate',
	'min_total_available_length',
	'max_total_available_length',
	'current_length_min',
	'current_length_max',
	'purchase_order_number',
	'length_friendly_id',
	'height_min',
	'height_max',
	'width_min',
	'width_max',
	'outer_diameter_min',
	'outer_diameter_max',
	'inner_diameter_min',
	'inner_diameter_max',
	'height_od_min',
	'height_od_max',
	'width_id_min',
	'width_id_max',
	'wall_min',
	'wall_max',
	'sort_by',
	'ordering',
] as const;

function buildApiQuery(
	searchParams: URLSearchParams,
	pageSize: number
): Record<string, string | number> {
	const query: Record<string, string | number> = {
		page: parseInt(searchParams.get('page') || '1', 10),
		page_size: pageSize,
	};
	const hasIndependentDimensionFilter = [
		'height_min',
		'height_max',
		'width_min',
		'width_max',
		'outer_diameter_min',
		'outer_diameter_max',
		'inner_diameter_min',
		'inner_diameter_max',
	].some((key) => searchParams.get(key));
	const legacyCombinedDimensionKeys = [
		'height_od_min',
		'height_od_max',
		'width_id_min',
		'width_id_max',
	];
	for (const key of FILTER_PARAM_KEYS) {
		if (hasIndependentDimensionFilter && legacyCombinedDimensionKeys.includes(key)) {
			continue;
		}
		const value = searchParams.get(key);
		if (value) query[key] = value;
	}
	return query;
}

/**
 * Params for dimension-ranges: only "context" filters (shape, category, types, etc.).
 * Excludes dimension range params so the API returns full min/max for that context;
 * slider can always use full range so user can adjust without reset.
 */
function buildRangeQueryParams(
	searchParams: URLSearchParams
): Record<string, string> {
	const RANGE_AFFECTING_KEYS = [
		'shape',
		'category',
		'types',
		'inventory_location',
		'status',
		'has_certificate',
		'purchase_order_number',
	];
	const params: Record<string, string> = {};
	for (const key of RANGE_AFFECTING_KEYS) {
		const value = searchParams.get(key);
		if (value) params[key] = value;
	}
	return params;
}

// ─── Page ────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const TabletMaterialLengthsPage = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { buildingId, buildingName } = useBuildingContext();

	// ── Data queries ─────────────────────────────────────────────────────────
	const { data: shapes } = useGetMaterialShapesQuery();
	const { data: categories } = useGetMaterialCategoriesQuery();
	const { data: locationsData } = useGetInventoryLocationsQuery(
		buildingId ? { building: buildingId } : {}
	);
	const shelvesQuery = useGetMaterialShelvesQuery();

	const category = searchParams.get('category') || '';
	const status = searchParams.get('status') || '';
	const currentPage = parseInt(searchParams.get('page') || '1', 10);

	const typesQuery = useGetMaterialTypesQuery(
		{ material_category: category },
		{ skip: !category, refetchOnMountOrArgChange: true }
	);

	const rangeParams = useMemo(
		() => buildRangeQueryParams(searchParams),
		[searchParams]
	);

	const { data: dimensionRanges } = useGetMaterialLengthDimensionRangesQuery(
		rangeParams,
		{ refetchOnMountOrArgChange: true }
	);

	const apiQuery = useMemo(
		() => {
			const query = buildApiQuery(searchParams, PAGE_SIZE);
			if (buildingId) query.building = buildingId;
			return query;
		},
		[searchParams, buildingId]
	);

	const { data, isLoading, isFetching, refetch } =
		useGetAllMaterialLengthsQuery(apiQuery, {
			refetchOnMountOrArgChange: true,
		});
	const [deleteMaterialLength] = useDeleteMaterialLengthMutation();
	const [recordAttributionCost] = useRecordAttributionCostMutation();

	// ── Modal state ──────────────────────────────────────────────────────────
	const [selectedMaterialLength, setSelectedMaterialLength] =
		useState<any>(null);
	const [isMaterialReservationModalOpen, setIsMaterialReservationModalOpen] =
		useState(false);
	const [isSplitLengthModalOpen, setIsSplitLengthModalOpen] = useState(false);
	const [isModifyLengthsModalOpen, setIsModifyLengthsModalOpen] =
		useState(false);
	const [isAddMaterialLengthsModalOpen, setIsAddMaterialLengthsModalOpen] =
		useState(false);
	const [isMoveMaterialModalOpen, setIsMoveMaterialModalOpen] = useState(false);
	const [moveSummaryData, setMoveSummaryData] =
		useState<MoveMaterialSummaryData | null>(null);

	// ── Row selection state ──────────────────────────────────────────────────
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [selectedRows, setSelectedRows] = useState<any[]>([]);
	const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
	const shouldClearModifySelectionOnCloseRef = useRef(false);

	// ── Dynamic table height ─────────────────────────────────────────────────
	const tableContainerRef = useRef<HTMLDivElement>(null);
	const [tableScrollY, setTableScrollY] = useState<string>('400px');

	const recalcScrollY = useCallback(() => {
		const container = tableContainerRef.current;
		if (!container) return;

		requestAnimationFrame(() => {
			const containerH = container.clientHeight;
			const header = container.querySelector('.ant-table-thead');
			const pagination = container.querySelector('.ant-pagination');

			const headerH = header?.getBoundingClientRect().height || 55;
			const paginationH = pagination?.getBoundingClientRect().height || 32;

			const newY = Math.max(containerH - headerH - paginationH - 4, 150);
			setTableScrollY(`${newY}px`);
		});
	}, []);

	useEffect(() => {
		recalcScrollY();
	}, [recalcScrollY]);

	useEffect(() => {
		const container = tableContainerRef.current;
		if (!container) return;

		const observer = new ResizeObserver(() => {
			recalcScrollY();
		});

		observer.observe(container);
		return () => observer.disconnect();
	}, [recalcScrollY]);

	const handleSelectionChange = (keys: React.Key[]) => {
		setSelectedRowKeys(keys);
		setSelectedRows((prevRows) => {
			const kept = prevRows.filter((r) => keys.includes(r.id));
			const keptIds = new Set(kept.map((r) => r.id));
			const added = (data?.results || []).filter(
				(r: any) => keys.includes(r.id) && !keptIds.has(r.id)
			);
			return [...kept, ...added];
		});
	};

	const handleRemoveSelectedLength = useCallback(
		(id: string) => {
			const target = String(id);
			setSelectedRowKeys((prev) => {
				const next = prev.filter((k) => String(k) !== target);
				if (next.length === 0) {
					setIsModifyLengthsModalOpen(false);
					setIsMoveMaterialModalOpen(false);
				}
				return next;
			});
			setSelectedRows((prev) => prev.filter((r) => String(r.id) !== target));
		},
		[]
	);

	const hasSelection = selectedRowKeys.length > 0;
	const hasSingleSelection = selectedRowKeys.length === 1;

	// ── Handlers ─────────────────────────────────────────────────────────────
	const handleFilterChange = (key: string, value: any) => {
		const params = new URLSearchParams(searchParams.toString());
		if (value) {
			params.set(key, value);
		} else {
			params.delete(key);
		}
		params.set('page', '1');
		navigate({ search: params.toString() });
	};

	const handlePageChange = (newPage: number) => {
		const params = new URLSearchParams(searchParams.toString());
		params.set('page', newPage.toString());
		navigate({ search: params.toString() });
	};

	// ── Action handlers ──────────────────────────────────────────────────
	const handleReserve = (record: any) => {
		setSelectedMaterialLength({
			...record,
			material: {
				id: record.material_id,
				total_available_length: record.material_total_available_length,
			},
		});
		setIsMaterialReservationModalOpen(true);
	};

	const handleDelete = (record: any) => {
		Modal.confirm({
			title: 'Delete Material Length?',
			content: `Are you sure you want to delete material length ${record.friendly_id}?`,
			okText: 'Yes, Delete',
			okType: 'danger',
			cancelText: 'Cancel',
			onOk: async () => {
				try {
					await deleteMaterialLength({ id: record.id }).unwrap();
					refetch();
				} catch (err) {
					console.error('Failed to delete:', err);
				}
			},
		});
	};

	const handleSplit = (record: any) => {
		setSelectedMaterialLength(record);
		setIsSplitLengthModalOpen(true);
	};

	const handleSplitFromSelection = () => {
		if (selectedRows.length === 1) {
			handleSplit(selectedRows[0]);
		}
	};

	const handleMove = () => {
		setIsMoveMaterialModalOpen(true);
	};

	const handleModify = () => {
		setIsModifyLengthsModalOpen(true);
	};

	const handleAdd = () => {
		setIsAddMaterialLengthsModalOpen(true);
	};

	// ── Derived data for modals ──────────────────────────────────────────
	const selectedLengthsForModal = useMemo(() => {
		return selectedRows.filter((r) => selectedRowKeys.includes(r.id));
	}, [selectedRows, selectedRowKeys]);

	const selectedMaterialForAdd = useMemo(() => {
		if (selectedRows.length !== 1) return null;
		const row = selectedRows[0];
		return {
			id: row.material_id,
			type: row.material_type,
			shape: row.material_shape,
			height: row.height,
			width: row.width,
			outer_diameter: row.outer_diameter,
			inner_diameter: row.inner_diameter,
			wall_thickness: row.wall_thickness,
		} as Material;
	}, [selectedRows]);

	// ── Close handlers ───────────────────────────────────────────────────
	const handleCloseReservationModal = () => {
		setIsMaterialReservationModalOpen(false);
		setSelectedMaterialLength(null);
		setSelectedRowKeys([]);
		setSelectedRows([]);
	};

	const handleCloseSplitModal = () => {
		setIsSplitLengthModalOpen(false);
		setSelectedMaterialLength(null);
		setSelectedRowKeys([]);
		setSelectedRows([]);
	};

	const handleCloseModifyLengthsModal = () => {
		setIsModifyLengthsModalOpen(false);
		if (shouldClearModifySelectionOnCloseRef.current) {
			setSelectedRowKeys([]);
			setSelectedRows([]);
			shouldClearModifySelectionOnCloseRef.current = false;
		}
	};

	const handleCloseMoveMaterialModal = () => {
		setIsMoveMaterialModalOpen(false);
	};

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100vh',
				padding: 12,
				background: '#f5f5f5',
				overflow: 'hidden',
			}}
		>
			{/* ── Header toolbar ─────────────────────────────────────────── */}
			<div
				style={{
					flexShrink: 0,
					display: 'grid',
					gridTemplateColumns: 'minmax(150px, 0.7fr) minmax(420px, 1.4fr) minmax(420px, 1.2fr)',
					alignItems: 'center',
					gap: 12,
					padding: '4px 0 12px',
				}}
			>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 10,
						minWidth: 0,
					}}
				>
					<h1
						style={{
							fontSize: 28,
							lineHeight: 1,
							fontWeight: 700,
							margin: 0,
						}}
					>
						Material
						<br />
						Lengths
					</h1>
					<BuildingIndicator />
				</div>

				<Radio.Group
					value={status || 'All'}
					onChange={(e) =>
						handleFilterChange(
							'status',
							e.target.value === 'All' ? '' : e.target.value
						)
					}
					optionType="button"
					buttonStyle="solid"
					style={{
						display: 'flex',
						justifyContent: 'center',
						whiteSpace: 'nowrap',
					}}
				>
					{['In Stock', 'Awaiting Delivery', 'Quality Hold', 'Scrapped', 'All'].map(
						(option) => (
							<Radio.Button
								key={option}
								value={option}
								style={{
									fontSize: 13,
									height: 36,
									lineHeight: '34px',
									padding: '0 10px',
								}}
							>
								{option === 'Awaiting Delivery' ? 'Awaiting' : option}
							</Radio.Button>
						)
					)}
				</Radio.Group>

				<div
					style={{
						display: 'flex',
						justifyContent: 'flex-end',
						alignItems: 'center',
						gap: 10,
					}}
				>
					<Search
						placeholder="ID, location, PO"
						allowClear
						onSearch={(val) => handleFilterChange('search', val)}
						defaultValue={searchParams.get('search') || ''}
						size="large"
						style={{ width: 260 }}
					/>
					<Button
						icon={<FilterOutlined />}
						size="large"
						onClick={() => setIsFiltersModalOpen(true)}
					>
						Filters
					</Button>
					<Button
						icon={<ArrowLeftOutlined />}
						type="primary"
						size="large"
						onClick={() => navigate('/tablet/operations')}
						style={{ minWidth: 96 }}
					>
						Back
					</Button>
				</div>
			</div>

			{/* ── Table + Action bar container ─────────────────────────────── */}
			<div
				style={{
					flex: 1,
					marginTop: 8,
					minHeight: 0,
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
				}}
			>
				{/* Applied filters chips (only renders when at least one filter is active) */}
				<AppliedFiltersBar
					queryParams={searchParams}
					navigate={(opts) => navigate({ search: opts.search })}
					shapes={shapes || []}
					categories={categories || []}
					types={typesQuery.data || []}
					locations={locationsData?.results || []}
					dimensionRanges={dimensionRanges}
					style={{ marginBottom: 6 }}
				/>

				{/* Table */}
				<div
					ref={tableContainerRef}
					style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
				>
					<MaterialLengthsTable
						data={data?.results || []}
						loading={isLoading || isFetching}
						pagination={{
							current: currentPage,
							pageSize: PAGE_SIZE,
							total: data?.count || 0,
							onChange: handlePageChange,
						}}
						onReserve={handleReserve}
						onDelete={handleDelete}
						onSplit={handleSplit}
						queryParams={searchParams}
						navigate={navigate}
						locations={locationsData?.results || []}
						shapes={shapes || []}
						categories={categories || []}
						types={typesQuery.data || []}
						dimensionRanges={dimensionRanges}
						isTablet={true}
						selectedRowKeys={selectedRowKeys}
						onSelectionChange={handleSelectionChange}
						scrollY={tableScrollY}
					/>
				</div>

				{/* Bottom action bar */}
				<div
					style={{
						flexShrink: 0,
						position: 'sticky',
						bottom: 0,
						zIndex: 5,
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						flexWrap: 'wrap',
						gap: 10,
						padding: '6px 10px',
						marginTop: 0,
						background: '#fff',
						borderRadius: 4,
						border: '1px solid #d9d9d9',
					}}
				>
					{hasSelection && (
						<>
							<Tag color="blue" style={{ margin: 0, fontSize: 13 }}>
								{selectedRowKeys.length} selected
							</Tag>
							<Button
								type="link"
								size="small"
								onClick={() => handleSelectionChange([])}
								style={{ padding: 0, fontSize: 13 }}
							>
								Clear
							</Button>
							<div
								style={{
									width: 1,
									height: 24,
									background: '#d9d9d9',
								}}
							/>
						</>
					)}

					<Space size="middle">
						<Button
							size="large"
							icon={<SwapOutlined />}
							disabled={!hasSelection}
							onClick={handleMove}
							style={{ minWidth: 100, height: 44 }}
						>
							Move
						</Button>
						<Button
							size="large"
							icon={<EditOutlined />}
							disabled={!hasSelection}
							onClick={handleModify}
							style={{ minWidth: 100, height: 44 }}
						>
							Modify
						</Button>
						<Tooltip
							title={
								!hasSingleSelection
									? 'Select exactly 1 row to split'
									: undefined
							}
						>
							<Button
								size="large"
								icon={<SplitCellsOutlined />}
								disabled={!hasSingleSelection}
								onClick={handleSplitFromSelection}
								style={{ minWidth: 100, height: 44 }}
							>
								Split
							</Button>
						</Tooltip>
						<Button
							size="large"
							type="primary"
							icon={<PlusOutlined />}
							disabled={!hasSingleSelection}
							onClick={handleAdd}
							style={{ minWidth: 100, height: 44 }}
						>
							Add
						</Button>
					</Space>
				</div>
			</div>

			{/* ── Modals ─────────────────────────────────────────────────── */}
			<Modal
				title="Filters"
				open={isFiltersModalOpen}
				onCancel={() => setIsFiltersModalOpen(false)}
				footer={null}
				width="92vw"
				centered
				destroyOnClose
				styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
			>
				<MaterialLengthsFilter
					layout="tablet-inline"
					shapes={shapes || []}
					categories={categories || []}
					types={typesQuery.data || []}
					locations={locationsData?.results || []}
					queryParams={searchParams}
					navigate={navigate}
					heightRange={dimensionRanges?.height}
					widthRange={dimensionRanges?.width}
					outerDiameterRange={dimensionRanges?.outer_diameter}
					innerDiameterRange={dimensionRanges?.inner_diameter}
					wallThicknessRange={dimensionRanges?.wall}
					currentLengthRange={dimensionRanges?.current_length}
					visibleFilterKeys={[
						'purchase_order_number',
						'shape',
						'category',
						'types',
						'inventory_location',
						'height',
						'width',
						'outer_diameter',
						'inner_diameter',
						'wall_thickness',
						'current_length',
						'available_length',
						'has_certificate',
					]}
					filterGroups={[
						{
							label: 'Dimensions',
							keys: [
								'height',
								'outer_diameter',
								'width',
								'inner_diameter',
								'wall_thickness',
							],
						},
						{
							label: 'Length',
							keys: ['current_length', 'available_length'],
						},
					]}
					filtersExpanded
				/>
			</Modal>

			{isMaterialReservationModalOpen && selectedMaterialLength && (
				<MaterialReservationModal
					onClose={handleCloseReservationModal}
					selectedMaterialReservation={null}
					selectedMaterial={selectedMaterialLength.material}
					selectedMaterialLength={selectedMaterialLength}
					isMaterialReservationModalOpen={isMaterialReservationModalOpen}
					reserveMultipleLengths={false}
					refetchData={refetch}
				/>
			)}

			{isSplitLengthModalOpen && selectedMaterialLength && (
				<SplitLengthModal
					isSplitLengthModalOpen={isSplitLengthModalOpen}
					onClose={handleCloseSplitModal}
					material={
						{
							id: selectedMaterialLength.material_id,
							display_name: selectedMaterialLength.material_display_name,
							type: selectedMaterialLength.material_type,
							shape: selectedMaterialLength.material_shape,
							width: selectedMaterialLength.width,
							height: selectedMaterialLength.height,
							outer_diameter: selectedMaterialLength.outer_diameter,
							inner_diameter: selectedMaterialLength.inner_diameter,
							wall_thickness: selectedMaterialLength.wall_thickness,
							total_available_length:
								selectedMaterialLength.material_total_available_length,
						} as any
					}
					selectedMaterialLength={selectedMaterialLength}
					refetchData={refetch}
				/>
			)}

			<ModifyLengthsModal
				open={isModifyLengthsModalOpen}
				onClose={handleCloseModifyLengthsModal}
				selectedLengths={selectedLengthsForModal}
				isTablet
				onRemoveLength={handleRemoveSelectedLength}
				onConfirm={async (operatorId, modifications) => {
					try {
						await recordAttributionCost({
							operator_id: String(operatorId),
							modifications,
						}).unwrap();
						refetch();
						shouldClearModifySelectionOnCloseRef.current = true;
					} catch (err) {
						console.error('Failed to record attribution cost:', err);
					}
				}}
			/>

			<MoveMaterialModal
				open={isMoveMaterialModalOpen}
				onClose={handleCloseMoveMaterialModal}
				selectedLengths={selectedLengthsForModal}
				shelves={shelvesQuery.data}
				isTablet
				onRemoveLength={handleRemoveSelectedLength}
				onConfirmSuccess={(data) => setMoveSummaryData(data)}
				refetch={refetch}
			/>

			{moveSummaryData && (
				<MaterialLocationsSummaryModal
					visible={true}
					onComplete={() => setMoveSummaryData(null)}
					materialInfo={moveSummaryData.materialInfo}
					lengths={moveSummaryData.lengths}
					title="Material Locations Summary"
				/>
			)}

		{isAddMaterialLengthsModalOpen && selectedMaterialForAdd && (
			<AddMaterialLengthsModal
				isAddMaterialLengthsModalOpen={isAddMaterialLengthsModalOpen}
				onClose={() => {
					setIsAddMaterialLengthsModalOpen(false);
				}}
				material={selectedMaterialForAdd}
				refetchData={refetch}
			building_id={buildingId ?? undefined}
			building_name={buildingName ?? undefined}
		/>
		)}
		</div>
	);
};

export default TabletMaterialLengthsPage;
